import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Mail } from "lucide-react";
import { BUSINESS, STATS } from "@/data/site";

const LINES = [
  { text: "La salute dei", italic: false },
  { text: "lavoratori,", italic: true },
  { text: "in regola.", italic: false },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative overflow-hidden bg-paper pt-28 md:pt-36"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-mono text-[11px] tracking-[0.3em] uppercase text-clinic mb-8 text-center"
        >
          Studio di Medicina del Lavoro — Roma · D.Lgs 81/08
        </motion.p>

        <h1 className="font-display text-[13vw] sm:text-6xl lg:text-[5.6rem] leading-[0.98] tracking-tight text-ink max-w-5xl mx-auto text-center">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.italic ? "italic text-clinic" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 grid gap-10 lg:grid-cols-12 lg:items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="lg:col-span-5"
          >
            <p className="text-base md:text-lg leading-relaxed text-ink/70 max-w-md">
              BPMedLav è lo studio dei medici competenti Dr. Giuseppe Buomprisco e Dr. Roberto
              Perri. Sorveglianza sanitaria, visite in azienda e formazione per mettere la tua
              impresa in regola con il D.Lgs 81/08.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={`mailto:${BUSINESS.email}?subject=Richiesta%20preventivo%20sorveglianza%20sanitaria`}
                data-testid="hero-cta-email"
                className="inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 font-mono text-[11px] tracking-[0.15em] uppercase text-paper transition-transform hover:-translate-y-0.5"
              >
                <Mail size={14} /> Richiedi preventivo
              </a>
              <a
                href="#servizi"
                data-testid="hero-cta-servizi"
                className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 font-mono text-[11px] tracking-[0.15em] uppercase text-ink transition-colors hover:border-clinic hover:text-clinic"
              >
                Scopri i servizi <ArrowDown size={14} />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="lg:col-span-7 relative"
          >
            <div className="relative h-72 md:h-96 overflow-hidden border border-ink/10">
              <motion.img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&q=80"
                alt="Medico competente in consultazione presso lo studio BPMedLav di Roma"
                style={{ y: imgY }}
                className="absolute inset-0 h-[120%] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 left-5 md:left-8 bg-clinic text-white px-6 py-4">
              <p className="font-display text-3xl font-semibold">+500</p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase">Aziende servite</p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-16 grid grid-cols-3 divide-x divide-ink/10 border-t border-ink/10"
        >
          {STATS.map((s) => (
            <div key={s.label} className="py-6 px-3 md:px-6">
              <p className="font-display text-2xl md:text-4xl text-ink">{s.value}</p>
              <p className="font-mono text-[9px] md:text-[10px] tracking-[0.2em] uppercase text-ink/50 mt-1">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
